import { create } from 'zustand';
import { newId } from '@/lib/id';
import { getApp } from '@/apps/registry';
import { DEFAULT_MIN_SIZE, Z_BASE } from './constants';
import { cascade, centre, clampRect, fitSize, maximizedRect, resizeRect, viewport } from './placement';
import type { Viewport } from './placement';
import type { AppArgs, OpenOptions, Rect, ResizeDir, WindowState, WmHandle } from './types';

/**
 * Ceiling for `nextZ`. The shell chrome is fixed at 9000 (menu popups) and
 * 10000 (taskbar), so the counter is rebased long before a window could climb
 * over either of them.
 */
const Z_CEILING = Z_BASE + 1000;

interface WindowStore {
  windows: WindowState[];
  nextZ: number;
  /** Monotonic counter so the cascade keeps advancing across closes. */
  openCount: number;
  /** True while a drag/resize is in progress — iframes are shielded. */
  interacting: boolean;

  open(appId: string, args?: AppArgs, opts?: OpenOptions): string | undefined;
  close(id: string): void;
  closeAll(): void;
  focus(id: string): void;
  blurAll(): void;
  minimize(id: string): void;
  restore(id: string): void;
  toggleMaximize(id: string): void;
  toggleFromTaskbar(id: string): void;
  move(id: string, x: number, y: number): void;
  setRect(id: string, rect: Rect): void;
  resizeBy(id: string, dir: ResizeDir, start: Rect, dx: number, dy: number): void;
  resize(id: string, w: number, h: number): void;
  setTitle(id: string, title: string): void;
  setIcon(id: string, icon: string): void;
  setArgs(id: string, args: AppArgs): void;
  setInteracting(value: boolean): void;
  /** Re-clamp every window after the browser window changes size. */
  reclampAll(): void;
}

function topmostVisible(windows: WindowState[]): WindowState | undefined {
  return windows
    .filter((w) => !w.minimized)
    .reduce<WindowState | undefined>((best, w) => (!best || w.zIndex > best.zIndex ? w : best), undefined);
}

function withFocus(windows: WindowState[], id: string | null): WindowState[] {
  return windows.map((w) => (w.focused === (w.id === id) ? w : { ...w, focused: w.id === id }));
}

/** Every window whose `parentId` chain leads back to `id` — the dialogs it owns. */
function ownedBy(windows: WindowState[], id: string): Set<string> {
  const owned = new Set<string>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const w of windows) {
      if (owned.has(w.id) || !w.parentId) continue;
      if (w.parentId === id || owned.has(w.parentId)) {
        owned.add(w.id);
        grew = true;
      }
    }
  }
  return owned;
}

/**
 * Raise `id` and stack the dialogs it owns directly above it. Ownership is a
 * real z-order relationship: a message box must never sink behind the window
 * that raised it, where it would have no taskbar button and no way back.
 */
function raiseFamily(
  windows: WindowState[],
  nextZ: number,
  id: string,
): { windows: WindowState[]; nextZ: number } {
  const owned = ownedBy(windows, id);
  const family = [
    id,
    ...windows
      .filter((w) => owned.has(w.id))
      .sort((a, b) => a.zIndex - b.zIndex)
      .map((w) => w.id),
  ];

  let z = nextZ;
  const zById = new Map<string, number>();
  for (const memberId of family) zById.set(memberId, ++z);

  return {
    windows: windows.map((w) => {
      const nz = zById.get(w.id);
      return nz === undefined ? w : { ...w, zIndex: nz, minimized: false };
    }),
    nextZ: z,
  };
}

/**
 * Renumber the stack from `Z_BASE` once the counter drifts too high, keeping
 * relative order. Without this a long-lived session eventually paints windows
 * over the taskbar.
 */
function rebase(windows: WindowState[], nextZ: number): { windows: WindowState[]; nextZ: number } {
  if (nextZ <= Z_CEILING) return { windows, nextZ };

  const zById = new Map<string, number>();
  [...windows]
    .sort((a, b) => a.zIndex - b.zIndex)
    .forEach((w, index) => zById.set(w.id, Z_BASE + 1 + index));

  return {
    windows: windows.map((w) => {
      const nz = zById.get(w.id) ?? w.zIndex;
      return nz === w.zIndex ? w : { ...w, zIndex: nz };
    }),
    nextZ: Z_BASE + windows.length,
  };
}

/** Shrink a rect to the viewport without dropping below the app's minimum size. */
function fitToViewport(rect: Rect, appId: string, vp: Viewport): Rect {
  const minSize = getApp(appId)?.minSize ?? DEFAULT_MIN_SIZE;
  const fitted = fitSize(rect, vp);
  return {
    ...rect,
    w: Math.max(Math.min(rect.w, minSize.w), fitted.w),
    h: Math.max(Math.min(rect.h, minSize.h), fitted.h),
  };
}

export const useWindowStore = create<WindowStore>((set, get) => ({
  windows: [],
  nextZ: Z_BASE,
  openCount: 0,
  interacting: false,

  open(appId, args = {}, opts = {}) {
    const manifest = getApp(appId);
    if (!manifest) {
      if (import.meta.env.DEV) console.warn(`[wm] unknown app "${appId}"`);
      return undefined;
    }

    const state = get();

    // Singletons focus and re-arm the existing instance instead of stacking up.
    if (manifest.singleton) {
      const existing = state.windows.find((w) => w.appId === appId);
      if (existing) {
        set((s) => {
          const raised = raiseFamily(s.windows, s.nextZ, existing.id);
          const rearmed = raised.windows.map((w) =>
            w.id === existing.id ? { ...w, args, argsVersion: w.argsVersion + 1 } : w,
          );
          const next = rebase(rearmed, raised.nextZ);
          return { windows: withFocus(next.windows, existing.id), nextZ: next.nextZ };
        });
        return existing.id;
      }
    }

    const vp = viewport();
    const isDialog = manifest.dialog === true;
    const parent = opts.parentId ? state.windows.find((w) => w.id === opts.parentId) : undefined;

    const base: Rect = isDialog
      ? centre(manifest.defaultSize, vp, parent)
      : cascade(state.openCount, manifest.defaultSize, vp);

    const rect = clampRect(
      {
        x: opts.rect?.x ?? base.x,
        y: opts.rect?.y ?? base.y,
        w: opts.rect?.w ?? base.w,
        h: opts.rect?.h ?? base.h,
      },
      vp,
    );

    const id = newId('win');
    const z = state.nextZ + 1;
    const shouldFocus = opts.focus !== false;

    const win: WindowState = {
      id,
      appId,
      title: opts.title ?? manifest.titleFor?.(args) ?? manifest.name,
      icon: opts.icon ?? manifest.icon,
      ...rect,
      minimized: false,
      maximized: false,
      zIndex: z,
      focused: shouldFocus,
      args,
      argsVersion: 0,
      dialog: isDialog,
      resizable: manifest.resizable !== false && !isDialog,
      maximizable: manifest.maximizable !== false && !isDialog,
      parentId: opts.parentId,
      createdAt: Date.now(),
    };

    set((s) => {
      const opened = shouldFocus ? [...withFocus(s.windows, null), win] : [...s.windows, win];
      const next = rebase(opened, z);
      return { windows: next.windows, nextZ: next.nextZ, openCount: s.openCount + 1 };
    });

    return id;
  },

  close(id) {
    set((s) => {
      // Closing a window also dismisses any dialogs it owns.
      const doomed = new Set([id]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const w of s.windows) {
          if (w.parentId && doomed.has(w.parentId) && !doomed.has(w.id)) {
            doomed.add(w.id);
            grew = true;
          }
        }
      }

      const remaining = s.windows.filter((w) => !doomed.has(w.id));
      const wasFocused = s.windows.some((w) => doomed.has(w.id) && w.focused);
      if (!wasFocused) return { windows: remaining };

      const next = topmostVisible(remaining);
      return { windows: withFocus(remaining, next?.id ?? null) };
    });
  },

  closeAll() {
    set({ windows: [] });
  },

  focus(id) {
    const s = get();
    const target = s.windows.find((w) => w.id === id);
    if (!target) return;
    if (target.focused && !target.minimized) return;

    const raised = raiseFamily(s.windows, s.nextZ, id);
    const next = rebase(raised.windows, raised.nextZ);
    set({ windows: withFocus(next.windows, id), nextZ: next.nextZ });
  },

  blurAll() {
    set((s) => ({ windows: withFocus(s.windows, null) }));
  },

  minimize(id) {
    set((s) => {
      // Owned dialogs go down with their owner — left behind they would float
      // over the desktop with nothing to return them to.
      const owned = ownedBy(s.windows, id);
      const windows = s.windows.map((w) =>
        w.id === id || owned.has(w.id) ? { ...w, minimized: true, focused: false } : w,
      );
      const next = topmostVisible(windows);
      return { windows: withFocus(windows, next?.id ?? null) };
    });
  },

  restore(id) {
    get().focus(id);
  },

  toggleMaximize(id) {
    const vp = viewport();
    set((s) => ({
      windows: s.windows.map((w) => {
        if (w.id !== id || !w.maximizable) return w;
        if (w.maximized) {
          // The desktop may have shrunk while it was maximized, so re-fit the
          // size as well as the position.
          const stored = w.restoreRect ?? { x: w.x, y: w.y, w: w.w, h: w.h };
          const r = clampRect(fitToViewport(stored, w.appId, vp), vp);
          return { ...w, maximized: false, restoreRect: undefined, ...r };
        }
        const max = maximizedRect(vp);
        return {
          ...w,
          maximized: true,
          restoreRect: { x: w.x, y: w.y, w: w.w, h: w.h },
          ...max,
        };
      }),
    }));
  },

  toggleFromTaskbar(id) {
    const w = get().windows.find((win) => win.id === id);
    if (!w) return;
    if (w.minimized) get().focus(id);
    else if (w.focused) get().minimize(id);
    else get().focus(id);
  },

  move(id, x, y) {
    set((s) => ({
      windows: s.windows.map((w) => (w.id === id ? { ...w, x, y } : w)),
    }));
  },

  setRect(id, rect) {
    set((s) => ({
      windows: s.windows.map((w) => (w.id === id ? { ...w, ...rect } : w)),
    }));
  },

  resize(id, w, h) {
    const win = get().windows.find((x) => x.id === id);
    // A maximized window owns its own geometry; leave it alone.
    if (!win || win.maximized) return;
    const min = getApp(win.appId)?.minSize ?? DEFAULT_MIN_SIZE;
    const size = fitSize({ w: Math.max(w, min.w), h: Math.max(h, min.h) });
    if (size.w === win.w && size.h === win.h) return;
    get().setRect(id, clampRect({ ...win, ...size }));
  },

  resizeBy(id, dir, start, dx, dy) {
    const manifest = getApp(get().windows.find((w) => w.id === id)?.appId ?? '');
    const minSize = manifest?.minSize ?? DEFAULT_MIN_SIZE;
    const next = resizeRect(start, dir, dx, dy, minSize);
    get().setRect(id, next);
  },

  setTitle(id, title) {
    set((s) => ({
      windows: s.windows.map((w) => (w.id === id && w.title !== title ? { ...w, title } : w)),
    }));
  },

  setIcon(id, icon) {
    set((s) => ({
      windows: s.windows.map((w) => (w.id === id && w.icon !== icon ? { ...w, icon } : w)),
    }));
  },

  setArgs(id, args) {
    set((s) => ({
      windows: s.windows.map((w) =>
        w.id === id ? { ...w, args, argsVersion: w.argsVersion + 1 } : w,
      ),
    }));
  },

  setInteracting(value) {
    if (get().interacting === value) return;
    set({ interacting: value });
    if (typeof document !== 'undefined') {
      if (value) document.body.setAttribute('data-interacting', 'true');
      else document.body.removeAttribute('data-interacting');
    }
  },

  reclampAll() {
    const vp = viewport();
    set((s) => ({
      windows: s.windows.map((w) => {
        if (w.maximized) return { ...w, ...maximizedRect(vp) };
        // Re-fit the size too: a position-only clamp leaves a non-resizable
        // window taller than the desktop with its buttons off-screen. Then pull
        // it back inside the new desktop so those buttons stay reachable.
        const fitted = fitToViewport({ x: w.x, y: w.y, w: w.w, h: w.h }, w.appId, vp);
        const x = Math.max(0, Math.min(fitted.x, vp.w - fitted.w));
        const y = Math.max(0, Math.min(fitted.y, vp.h - fitted.h));
        return { ...w, ...clampRect({ ...fitted, x, y }, vp) };
      }),
    }));
  },
}));

/* ------------------------------------------------------------------ */
/* Per-window handles                                                   */
/* ------------------------------------------------------------------ */

const handleCache = new Map<string, WmHandle>();

/**
 * A stable `WmHandle` for a window id. Cached so app components don't see a
 * new `wm` prop on every render.
 */
export function handleFor(windowId: string): WmHandle {
  const cached = handleCache.get(windowId);
  if (cached) return cached;

  const handle: WmHandle = {
    windowId,
    setTitle: (title) => useWindowStore.getState().setTitle(windowId, title),
    setIcon: (icon) => useWindowStore.getState().setIcon(windowId, icon),
    close: () => useWindowStore.getState().close(windowId),
    focus: () => useWindowStore.getState().focus(windowId),
    minimize: () => useWindowStore.getState().minimize(windowId),
    toggleMaximize: () => useWindowStore.getState().toggleMaximize(windowId),
    resize: (w, h) => useWindowStore.getState().resize(windowId, w, h),
    open: (appId, args, opts) => useWindowStore.getState().open(appId, args, opts),
    openDialog: (appId, args, opts) =>
      useWindowStore.getState().open(appId, args, { ...opts, parentId: windowId }),
    isFocused: () => useWindowStore.getState().windows.find((w) => w.id === windowId)?.focused ?? false,
  };

  handleCache.set(windowId, handle);
  return handle;
}

/** Drop handles for windows that no longer exist. Called by `WindowLayer`. */
export function pruneHandles(liveIds: Set<string>): void {
  for (const id of handleCache.keys()) {
    if (!liveIds.has(id)) handleCache.delete(id);
  }
}

/* ------------------------------------------------------------------ */
/* Selectors                                                            */
/* ------------------------------------------------------------------ */

/** Windows that deserve a taskbar button, in creation order. */
export const selectTaskbarWindows = (s: WindowStore): WindowState[] =>
  s.windows.filter((w) => !w.dialog).sort((a, b) => a.createdAt - b.createdAt);

export const selectFocused = (s: WindowStore): WindowState | undefined =>
  s.windows.find((w) => w.focused);
